
import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  HiOutlineScale,
  HiOutlineShieldCheck,
  HiOutlineBookOpen,
  HiOutlineUserGroup,
  HiOutlineBriefcase,
  HiOutlineBuildingOffice2,
  HiOutlineDocumentText,
  HiOutlineMagnifyingGlass,
  HiOutlineClipboardDocumentCheck,
  HiOutlineChatBubbleLeftRight,
  HiOutlineFlag,
  HiOutlineEye,
  HiOutlineLockClosed,
  HiOutlineChevronDown,
  HiOutlineArrowUpRight,
  HiOutlineArrowDown,
  HiOutlineCheckCircle,
  HiOutlineAcademicCap,
  HiOutlineQuestionMarkCircle,
} from "react-icons/hi2";

import { Header, Fotter } from "../components/layout";
import { aboutUsData } from "../constants";

/* ============================================
   ICONS
============================================ */

const featureIcons = [
  HiOutlineShieldCheck,
  HiOutlineBookOpen,
  HiOutlineCheckCircle,
];

const expertiseIcons = [
  HiOutlineScale,
  HiOutlineAcademicCap,
  HiOutlineBriefcase,
  HiOutlineUserGroup,
  HiOutlineBuildingOffice2,
  HiOutlineShieldCheck,
];

const processIcons = [
  HiOutlineMagnifyingGlass,
  HiOutlineDocumentText,
  HiOutlineClipboardDocumentCheck,
  HiOutlineChatBubbleLeftRight,
];

const valueIcons = [
  HiOutlineLockClosed,
  HiOutlineMagnifyingGlass,
  HiOutlineChatBubbleLeftRight,
];

/* ============================================
   REUSABLE SECTION HEADER
============================================ */

function SectionHeading({
  eyebrow,
  title,
  description,
  center = false,
  light = false,
}) {
  return (
    <div
      className={`max-w-[760px] ${
        center ? "mx-auto text-center" : ""
      }`}
    >
      <div
        className={`mb-5 flex items-center gap-3 ${
          center ? "justify-center" : ""
        }`}
      >
        <span
          className="h-px w-8 bg-skin-accent"
          aria-hidden="true"
        />

        <span
          className={
            light
              ? "eyebrow text-[#D7B77F]"
              : "eyebrow text-skin-accent"
          }
        >
          {eyebrow}
        </span>

        {center && (
          <span
            className="h-px w-8 bg-skin-accent"
            aria-hidden="true"
          />
        )}
      </div>

      <h2
        className={`section-title ${
          light
            ? "text-white"
            : "text-skin-heading"
        }`}
      >
        {title}
      </h2>

      {description && (
        <p
          className={`section-description mt-5 ${
            light
              ? "text-[#C6D3DE]"
              : "text-skin-muted"
          }`}
        >
          {description}
        </p>
      )}
    </div>
  );
}

/* ============================================
   ABOUT US PAGE
============================================ */

function AboutUs() {
  const { i18n } = useTranslation();

  // i18n is used only for choosing content.
  // Direction is managed globally by the project.
  const language = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en")
    ? "en"
    : "ar";

  const content = aboutUsData[language];

  return (
    <>
      <Header active={2} />

      <main className="font-kufi overflow-hidden bg-skin-page text-skin-text transition-colors duration-300">

        {/* ======================================
            01 — HERO
        ====================================== */}

        <section className="relative isolate overflow-hidden bg-[#102031] text-white">

          {/* Background */}
          <img
            src={aboutUsData.images.page}
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            className="absolute inset-0 h-full w-full object-cover object-center"
          />

          {/* Image overlays */}
          <div className="absolute inset-0 bg-[#071522]/65" />

          <div className="absolute inset-0 bg-gradient-to-t from-[#071522]/95 via-[#071522]/55 to-[#071522]/30" />

          {/* Decorative border */}
          <div
            aria-hidden="true"
            className="absolute bottom-0 h-px w-full bg-white/15"
          />

          <div className="container relative z-10 flex min-h-[620px] flex-col justify-end pb-28 pt-40 md:min-h-[700px] md:pb-32">

            <div className="grid items-end gap-12 lg:grid-cols-[minmax(0,1fr)_300px] xl:gap-20">

              {/* Hero content */}
              <div className="max-w-[850px]">

                {/* Breadcrumb */}
                <div className="mb-9 flex items-center gap-3">

                  <span className="body-3 text-white/65">
                    {content.home}
                  </span>

                  <span className="h-px w-7 bg-[#D7B77F]" />

                  <span className="body-3 text-[#D7B77F]">
                    {content.about}
                  </span>

                </div>

                {/* Eyebrow */}
                <div className="mb-5 flex items-center gap-3">

                  <span className="h-px w-9 bg-[#D7B77F]" />

                  <span className="eyebrow text-[#D7B77F]">
                    {content.hero.eyebrow}
                  </span>

                </div>

                {/* Main title */}
                <h1 className="hero-title max-w-[850px] text-white">

                  <span className="block">
                    {content.hero.title}
                  </span>

                  <span className="block text-[#E6E0D7]">
                    {content.hero.highlight}
                  </span>

                </h1>

                {/* Description */}
                <p className="lead mt-7 max-w-[700px] text-white/80">
                  {content.hero.description}
                </p>

                {/* Actions */}
                <div className="mt-10 flex flex-wrap gap-3">

                  <a
                    href="#who-we-are"
                    className="button group inline-flex min-h-[54px] items-center justify-center gap-3 rounded-xl border border-white bg-white px-6 text-[#102031] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#E8EEF2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D7B77F]"
                  >
                    {content.hero.discover}

                    <HiOutlineArrowDown
                      size={19}
                      aria-hidden="true"
                      className="transition-transform group-hover:translate-y-1"
                    />
                  </a>

                  <a
                    href="#legal-expertise"
                    className="button group inline-flex min-h-[54px] items-center justify-center gap-3 rounded-xl border border-white/25 bg-white/[0.06] px-6 text-white backdrop-blur-md transition-all duration-300 hover:border-white/45 hover:bg-white/[0.12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D7B77F]"
                  >
                    {content.hero.expertise}

                    <HiOutlineArrowUpRight
                      size={19}
                      aria-hidden="true"
                    />
                  </a>

                </div>

              </div>

              {/* Hero side statement */}
              <aside className="hidden rounded-2xl border border-white/15 bg-[#102031]/60 p-7 backdrop-blur-xl lg:block">

                <HiOutlineScale
                  size={34}
                  className="mb-7 text-[#D7B77F]"
                  aria-hidden="true"
                />

                <h2 className="h6 text-white">
                  {content.hero.sideTitle}
                </h2>

                <p className="body-2 mt-4 text-white/70">
                  {content.hero.sideDescription}
                </p>

                <div className="my-6 h-px bg-white/15" />

                <p className="caption text-[#D7B77F]">
                  {content.hero.sideNote}
                </p>

              </aside>

            </div>

          </div>
        </section>

        {/* ======================================
            02 — PROFESSIONAL HIGHLIGHTS
        ====================================== */}

        <section className="relative z-20 -mt-10">
          <div className="container">

            <div className="grid overflow-hidden rounded-2xl border border-skin-border bg-skin-surface shadow-soft md:grid-cols-3">

              {content.hero.traits.map((trait, index) => {
                const Icon =
                  featureIcons[index % featureIcons.length];

                return (
                  <div
                    key={index}
                    className="flex items-center gap-4 border-b border-skin-border px-6 py-6 last:border-b-0 md:border-b-0 md:border-e md:last:border-e-0 lg:px-8"
                  >

                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-skin-soft text-skin-accent">

                      <Icon
                        size={24}
                        aria-hidden="true"
                      />

                    </div>

                    <div>
                      <span className="caption text-skin-muted">
                        {String(index + 1).padStart(2, "0")}
                      </span>

                      <h3 className="h6 text-skin-heading">
                        {trait}
                      </h3>
                    </div>

                  </div>
                );
              })}

            </div>

          </div>
        </section>

        {/* ======================================
            03 — WHO WE ARE
        ====================================== */}

        <section
          id="who-we-are"
          className="scroll-mt-24 py-24 md:py-32"
        >
          <div className="container">

            {/* Text first:
                RTL -> right
                LTR -> left */}

            <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">

              {/* Content */}
              <div className="min-w-0">

                <SectionHeading
                  eyebrow={content.introduction.eyebrow}
                  title={content.introduction.title}
                />

                <div className="mt-8 flex flex-col gap-6">

                  {content.introduction.paragraphs.map(
                    (paragraph, index) => (
                      <p
                        key={index}
                        className="body-1 text-skin-text"
                      >
                        {paragraph}
                      </p>
                    )
                  )}

                </div>

                {/* Highlighted statement */}
                <div className="mt-9 flex items-start gap-4 border-s-2 border-skin-accent bg-skin-soft px-6 py-5">

                  <HiOutlineScale
                    size={26}
                    aria-hidden="true"
                    className="mt-1 shrink-0 text-skin-accent"
                  />

                  <p className="body-2 text-skin-heading">
                    {content.introduction.footnote}
                  </p>

                </div>

              </div>

              {/* Image */}
              <figure className="relative min-w-0">

                {/* Decorative frame */}
                <div
                  aria-hidden="true"
                  className="absolute -end-4 -top-4 h-32 w-32 border-e border-t border-skin-accent/60"
                />

                <div className="relative overflow-hidden rounded-[26px] border border-skin-border bg-skin-surface p-2 shadow-elevated">

                  <div className="relative overflow-hidden rounded-[19px]">

                    <img
                      src={aboutUsData.images.section}
                      alt={content.introduction.imageAlt}
                      loading="lazy"
                      decoding="async"
                      className="h-[380px] w-full object-cover sm:h-[490px] lg:h-[560px]"
                    />

                    <div
                      aria-hidden="true"
                      className="absolute inset-0 bg-gradient-to-t from-[#071522]/75 via-transparent to-transparent"
                    />

                    {/* Image caption */}
                    <figcaption className="absolute bottom-0 start-0 w-full p-5 sm:p-7">

                      <div className="max-w-[430px] rounded-xl border border-white/20 bg-[#102031]/75 p-5 text-white backdrop-blur-lg">

                        <HiOutlineScale
                          size={23}
                          aria-hidden="true"
                          className="mb-3 text-[#D7B77F]"
                        />

                        <p className="body-3 text-white/90">
                          {content.introduction.imageCaption}
                        </p>

                      </div>

                    </figcaption>

                  </div>
                </div>

              </figure>

            </div>
          </div>
        </section>

        {/* ======================================
            04 — LEGAL EXPERTISE
        ====================================== */}

        <section
          id="legal-expertise"
          className="scroll-mt-24 bg-skin-surface py-24 md:py-28"
        >
          <div className="container">

            <div className="mb-14">
              <SectionHeading
                eyebrow={content.expertise.eyebrow}
                title={content.expertise.title}
                description={content.expertise.description}
              />
            </div>

            {/* Expertise cards */}
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">

              {content.expertise.items.map((item, index) => {
                const Icon =
                  expertiseIcons[index % expertiseIcons.length];

                return (
                  <article
                    key={index}
                    className="group flex h-full flex-col rounded-2xl border border-skin-border bg-skin-page p-7 transition-all duration-300 hover:-translate-y-1 hover:border-skin-accent/40 hover:shadow-soft lg:p-8"
                  >

                    {/* Icon and number */}
                    <div className="mb-8 flex items-start justify-between">

                      <div className="flex h-13 w-13 h-[52px] w-[52px] items-center justify-center rounded-xl border border-skin-border bg-skin-surface text-skin-accent">

                        <Icon
                          size={27}
                          aria-hidden="true"
                        />

                      </div>

                      <span className="caption text-skin-muted">
                        {String(index + 1).padStart(2, "0")}
                      </span>

                    </div>

                    <h3 className="card-title text-skin-heading">
                      {item.title}
                    </h3>

                    <p className="card-description mt-4 flex-1 text-skin-muted">
                      {item.description}
                    </p>

                    <div className="mt-8 border-t border-skin-border pt-5">

                      <span className="caption text-skin-accent">
                        {content.expertise.cardLabel}
                      </span>

                    </div>

                  </article>
                );
              })}

            </div>

          </div>
        </section>

        {/* ======================================
            05 — WORK PROCESS
        ====================================== */}

        <section
          id="our-approach"
          className="relative scroll-mt-24 overflow-hidden bg-[#102031] py-24 text-white md:py-28"
        >

          {/* Subtle decorative lines */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -end-32 -top-32 h-[420px] w-[420px] rounded-full border border-white/[0.06]"
          />

          <div
            aria-hidden="true"
            className="pointer-events-none absolute -end-16 -top-16 h-[290px] w-[290px] rounded-full border border-white/[0.06]"
          />

          <div className="container relative">

            <div className="mb-16 grid gap-8 lg:grid-cols-2 lg:items-end">

              <SectionHeading
                eyebrow={content.process.eyebrow}
                title={content.process.title}
                light
              />

              <p className="section-description max-w-[550px] text-[#C6D3DE]">
                {content.process.description}
              </p>

            </div>

            {/* Process steps */}
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">

              {content.process.steps.map((step, index) => {
                const Icon =
                  processIcons[index % processIcons.length];

                return (
                  <article
                    key={index}
                    className="group border-t border-white/20 pt-7 transition-colors duration-300 hover:border-[#D7B77F]"
                  >

                    <div className="mb-9 flex items-center justify-between">

                      <span className="h4 text-white/30">
                        {String(index + 1).padStart(2, "0")}
                      </span>

                      <Icon
                        size={27}
                        aria-hidden="true"
                        className="text-[#D7B77F]"
                      />

                    </div>

                    <h3 className="card-title text-white">
                      {step.title}
                    </h3>

                    <p className="card-description mt-4 text-[#B2C4D3]">
                      {step.description}
                    </p>

                  </article>
                );
              })}

            </div>

          </div>
        </section>

        {/* ======================================
            06 — OUR VALUES
        ====================================== */}

        <section className="py-24 md:py-28">
          <div className="container">

            <div className="mb-14">
              <SectionHeading
                eyebrow={content.values.eyebrow}
                title={content.values.title}
                description={content.values.description}
                center
              />
            </div>

            <div className="grid gap-5 md:grid-cols-3">

              {content.values.items.map((item, index) => {
                const Icon =
                  valueIcons[index % valueIcons.length];

                return (
                  <article
                    key={index}
                    className="rounded-2xl border border-skin-border bg-skin-surface p-8 transition-colors duration-300 hover:border-skin-accent/40"
                  >

                    <div className="mb-8 flex items-center justify-between">

                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-skin-soft text-skin-accent">

                        <Icon
                          size={25}
                          aria-hidden="true"
                        />

                      </div>

                      <span className="caption text-skin-muted">
                        0{index + 1}
                      </span>

                    </div>

                    <h3 className="h5 text-skin-heading">
                      {item.title}
                    </h3>

                    <p className="body-2 mt-4 text-skin-muted">
                      {item.description}
                    </p>

                  </article>
                );
              })}

            </div>

          </div>
        </section>

        {/* ======================================
            07 — MISSION & VISION
        ====================================== */}

        <section className="bg-skin-soft py-24 md:py-28">
          <div className="container">

            <div className="mb-14">
              <SectionHeading
                eyebrow={content.purpose.eyebrow}
                title={content.purpose.title}
                description={content.purpose.description}
              />
            </div>

            <div className="grid gap-6 lg:grid-cols-2">

              {/* Mission */}
              <article className="relative overflow-hidden rounded-[26px] bg-[#19344D] p-8 text-white sm:p-11">

                <div
                  aria-hidden="true"
                  className="absolute -end-20 -top-20 h-60 w-60 rounded-full border border-white/10"
                />

                <HiOutlineFlag
                  size={34}
                  aria-hidden="true"
                  className="mb-10 text-[#D7B77F]"
                />

                <span className="eyebrow text-[#D7B77F]">
                  {content.mission.label}
                </span>

                <h3 className="h3 mt-4 text-white">
                  {content.mission.title}
                </h3>

                <div className="my-6 h-px w-12 bg-[#D7B77F]" />

                <p className="body-1 text-[#D9E4EC]">
                  {content.mission.description}
                </p>

              </article>

              {/* Vision */}
              <article className="rounded-[26px] border border-skin-border bg-skin-surface p-8 sm:p-11">

                <HiOutlineEye
                  size={34}
                  aria-hidden="true"
                  className="mb-10 text-skin-accent"
                />

                <span className="eyebrow text-skin-accent">
                  {content.vision.label}
                </span>

                <h3 className="h3 mt-4 text-skin-heading">
                  {content.vision.title}
                </h3>

                <div className="my-6 h-px w-12 bg-skin-accent" />

                <p className="body-1 text-skin-text">
                  {content.vision.description}
                </p>

              </article>

            </div>

          </div>
        </section>

        {/* ======================================
            08 — FREQUENTLY ASKED QUESTIONS
        ====================================== */}

        <section
          id="about-faq"
          className="scroll-mt-24 py-24 md:py-28"
        >
          <div className="container">

            <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">

              {/* FAQ introduction */}
              <div>

                <SectionHeading
                  eyebrow={content.faq.eyebrow}
                  title={content.faq.title}
                  description={content.faq.description}
                />

                <div className="mt-9 flex items-start gap-4 rounded-xl border border-skin-border bg-skin-soft p-6">

                  <HiOutlineQuestionMarkCircle
                    size={27}
                    aria-hidden="true"
                    className="shrink-0 text-skin-accent"
                  />

                  <p className="body-2 text-skin-text">
                    {content.faq.note}
                  </p>

                </div>

              </div>

              {/* Accordion */}
              <div className="space-y-3">

                {content.faq.items.map((item, index) => (
                  <details
                    key={index}
                    className="group overflow-hidden rounded-xl border border-skin-border bg-skin-surface transition-colors duration-300 open:border-skin-accent/40"
                  >

                    <summary className="flex cursor-pointer list-none items-center justify-between gap-5 px-6 py-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-skin-accent [&::-webkit-details-marker]:hidden">

                      <span className="flex min-w-0 items-center gap-4">

                        <span className="caption shrink-0 text-skin-accent">
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <span className="h6 text-skin-heading">
                          {item.question}
                        </span>

                      </span>

                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-skin-soft text-skin-heading">

                        <HiOutlineChevronDown
                          size={19}
                          aria-hidden="true"
                          className="transition-transform duration-300 group-open:rotate-180"
                        />

                      </span>

                    </summary>

                    <div className="border-t border-skin-border px-6 pb-6 pt-5">

                      <p className="body-2 text-skin-text">
                        {item.answer}
                      </p>

                    </div>

                  </details>
                ))}

              </div>

            </div>

          </div>
        </section>

        {/* ======================================
            09 — CALL TO ACTION
        ====================================== */}

        <section className="relative overflow-hidden bg-[#0B1726] py-24 text-white md:py-28">

          {/* Background ornament */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#102031] to-[#0B1726]"
          />

          <div className="container relative">

            <div className="mx-auto max-w-[800px] text-center">

              <div className="mx-auto mb-8 flex h-14 w-14 items-center justify-center rounded-xl border border-white/15 bg-white/[0.06] text-[#D7B77F]">

                <HiOutlineScale
                  size={29}
                  aria-hidden="true"
                />

              </div>

              <p className="eyebrow text-[#D7B77F]">
                {content.closing.eyebrow}
              </p>

              <h2 className="section-title mt-5 text-white">
                {content.closing.title}
              </h2>

              <p className="lead mx-auto mt-6 max-w-[670px] text-[#B2C4D3]">
                {content.closing.description}
              </p>

              {/* CTA actions */}
              <div className="mt-10 flex flex-wrap justify-center gap-3">

                <Link
                  to={aboutUsData.links?.contact || "/contact"}
                  className="button group inline-flex min-h-[54px] items-center justify-center gap-3 rounded-xl bg-white px-7 text-[#102031] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#E8EEF2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D7B77F]"
                >

                  {content.closing.contactButton}

                  <HiOutlineArrowUpRight
                    size={20}
                    aria-hidden="true"
                  />

                </Link>

                <a
                  href="#who-we-are"
                  className="button inline-flex min-h-[54px] items-center justify-center gap-3 rounded-xl border border-white/25 bg-white/[0.05] px-7 text-white transition-colors duration-300 hover:bg-white/[0.12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D7B77F]"
                >

                  {content.closing.backButton}

                  <HiOutlineArrowUpRight
                    size={20}
                    aria-hidden="true"
                  />

                </a>

              </div>

            </div>

          </div>
        </section>

      </main>

      <Fotter />
    </>
  );
}

export default AboutUs;
